/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import PropTypes from 'prop-types';

import { isVn } from '@global/helpers';

const propTypes = {
  testID: PropTypes.string,
  editable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  fields: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  isAll: PropTypes.bool,
  searchWithCode: PropTypes.bool,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  placeholder: PropTypes.string,
  useCallbackOptions: PropTypes.array,
};

const defaultProps = {
  testID: 'customer-search',
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  loading: false,
  customFilter: {},
  fields: '',
  value: '',
  isAll: false,
  label: 'Customer',
  labelKey: 'name',
  placeholder: 'Select Customer',
  searchWithCode: false,
  useCallbackOptions: [],
};

const limit = 10;

const CustomerSearch = (props) => {
  const {
    editable = true,
    isRequired = false,
    multiple = false,
    loading: isLoadingProps,
    customFilter = {},
    fields = '',
    hasError = false,
    value = '',
    isAll = false,
    label = 'Customer',
    labelKey = 'name',
    placeholder = 'Select Customer',
    searchWithCode = false,
    useCallbackOptions = [],
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getCustomers = useApi(MsAccount.getCustomers);

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading((prev) => ({ ...prev, loadMore: true }));
    } else {
      setLoading((prev) => ({ ...prev, initial: true }));
    }
    try {
      let filter = {};
      if (customFilter && Object.keys(customFilter).length) {
        filter = customFilter;
      }
      if (val) {
        filter['$or'] = [
          {
            name: { $regex: val, $options: 'i' },
          },
        ];
        if (isVn()) {
          filter['$or'].push({
            name_latin: { $regex: val, $options: 'i' },
          });
        }
        if (searchWithCode) {
          filter['$or'].push({
            customer_code: { $regex: val, $options: 'i' },
          });
        }
      }
      if (!val && (filter.name || filter.customer_code || filter['$or'])) {
        delete filter.name;
        delete filter.customer_code;
        delete filter['$or'];
      }

      const response = await getCustomers.request({
        params: {
          filter: filter,
          skip: newSkip,
          limit: limit,
          ...(fields ? { fields } : {}),
        },
      });
      if (response.status == 200 && response?.data?.data?.length) {
        const result = response.data.data.map((item) => {
          if (item._id) {
            item.id = item._id;
          }
          item.display_label = item.customer_code
            ? `${item.customer_code} - ${item.name}`
            : item.name;
          return item;
        });

        if (newSkip) {
          setOptions((prev) => prev.concat(result));
        } else {
          if (isAll) {
            result.unshift({ id: 'all', name: 'All' });
          }
          setOptions(result);
        }
      } else {
        if (!newSkip) {
          setOptions([]);
        }
      }

      if (newSkip) {
        setLoading((prev) => ({ ...prev, loadMore: false }));
      } else {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    } catch (error) {
      if (newSkip) {
        setLoading((prev) => ({ ...prev, loadMore: false }));
      } else {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    }
  };

  return (
    <SelectBox
      {...props}
      label={label}
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : placeholder
      }
      trackBy="id"
      labelKey={labelKey}
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial && !isLoadingProps}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={skip >= options.length}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      useCallbackOptions={useCallbackOptions}
      onChange={(val) => {
        if (isAll) {
          const allValue = { id: 'all', name: 'All' };
          if (multiple) {
            if (val?.length <= 0) {
              val.push(allValue);
            } else {
              const idx = val.findIndex((c) => c.id == 'all');
              if (idx > -1 && val?.length > 1) {
                const childIdx = value?.findIndex((c) => c.id == 'all');
                if (childIdx > -1) {
                  val.splice(idx, 1);
                } else {
                  val = [allValue];
                }
              }
            }
          } else {
            if (!val?.id) {
              val = allValue;
            }
          }
        }
        props.onChange(val);
      }}
      onReady={() => {
        if (!isLoadingProps) {
          setSkip(0);
          onSearch();
        }
      }}
    />
  );
};

CustomerSearch.propTypes = propTypes;
CustomerSearch.defaultProps = defaultProps;

export default CustomerSearch;
